import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, onBeforeUnmount, PropType, ref, shallowRef, watch } from 'vue';
import ChatRichTextEditor from '@/shared/components/ChatRichTextEditor.vue';
import ChatMessageActions from '@/modules/Chat/components/ChatMessageActions.vue';
import AttachmentList from '@/shared/components/AttachmentList.vue';
import Attachment from '@/models/Attachment';
import CMSUserRepository from '@/repositories/CMSUserRepository';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import SingleNextStepForm from '@/components/global/SingleNextStepForm.vue';
import { SingleNextStepFormReference } from '@/interfaces/components/nextStep/SingleNextStepFormReference';
import moment from 'moment';
import NextStepTypeRepository from '@/repositories/NextStepTypeRepository';
import ReminderOptionRepository from '@/repositories/ReminderOptionRepository';
import UpgradedNextStep from '@/models/interfaces/UpgradedNextStep';
import { saveChatMessage } from '@/modules/Chat/helpers/ChatHelper';
import i18n from '@/i18n';
import { ActivityFormOptions } from '@/interfaces/components/chat/ActivityFormOptions';
import { UploadFile } from 'ant-design-vue/types/upload';
import { UserRepository } from '@/repositories/UserRepository';
import User from '@/models/User';


export default /*#__PURE__*/_defineComponent({
  __name: 'ChatInputArea',
  props: {
    propChatId: {
        type: String as PropType<string | null>,
        default: null,
    },
    selectedProjectId: {
        type: String as PropType<string | null>,
        default: null,
    },
    isProjectInRequestState: {
        type: Boolean,
        default: false,
    },
},
  setup(__props) {

const props = __props;



const { proxy } = getCurrentInstance()!;
const vm = proxy as any;

const attachments = ref<Attachment[]>([]);
const content = ref('');
const isNextStepFormActive = ref(false);
const emptyNextStep = {
    nextStepType: null,
    reminderOption: null,
    assignedUser: null,
    date: null,
    name: '',
    note: '',
    completed: false,
    localKey: 0,
};
const firstSelectedUserId = ref<string | null>(null);
const singleNextStep = shallowRef<UpgradedNextStep | null>(null);
const singleNextStepFormReference = ref<SingleNextStepFormReference | null>(null);
const messageIsBeingCreated = ref(false);

const allUsers = computed(() => CMSUserRepository.getAll());
const currentUser = computed(() => UserRepository.getCurrentUser());
const nextStepTypes = computed(() => NextStepTypeRepository.getAll());
const reminderOptions = computed(() => ReminderOptionRepository.getAll());

function selectUser(userId: string) {
    if (firstSelectedUserId.value != null) {
        return;
    }
    firstSelectedUserId.value = userId;
}

function addEmptyNextStep(activityFormOptions?: ActivityFormOptions) {
    const currentUserId = (currentUser.value as User).id;

    if (activityFormOptions) {
        singleNextStep.value = {
            ...emptyNextStep,
            date: moment(),
            note: activityFormOptions.content,
            assignedUser: activityFormOptions.selectedUserId ? activityFormOptions.selectedUserId : currentUserId,
            localKey: 1,
            messageId: activityFormOptions.messageId,
        };
        return;
    }

    singleNextStep.value = {
        ...emptyNextStep,
        date: moment(),
        note: content.value,
        assignedUser: firstSelectedUserId.value ? firstSelectedUserId.value : currentUserId,
        localKey: 1,
    };
}

function pushToAttachments(attachment: Attachment) {
    attachments.value.push(attachment);
}

function onUpdateContent(updatedContent: string) {
    content.value = updatedContent;
}

async function onSaveMessage() {
    messageIsBeingCreated.value = true;

    try {
        await saveChatMessage({
            chatId: props.propChatId,
            selectedProjectId: props.selectedProjectId as string,
            content: content.value,
            attachments: attachments.value,
            singleNextStepFormReference: singleNextStepFormReference.value as unknown as SingleNextStepFormReference,
            shouldCreateNextStep: false,
        });
    } catch (e) {
        messageIsBeingCreated.value = false;
        vm.$notification.error({
            message: i18n.t('Došlo je do greške') as string,
            description: (e as Error).message,
        });
        return;
    }

    messageIsBeingCreated.value = false;
    onCancel();
}

async function onCreateActivity() {
    messageIsBeingCreated.value = true;

    try {
        await saveChatMessage({
            chatId: props.propChatId,
            selectedProjectId: props.selectedProjectId as string,
            content: content.value,
            attachments: attachments.value,
            singleNextStepFormReference: singleNextStepFormReference.value as unknown as SingleNextStepFormReference,
            chatMessageId: singleNextStep.value != null ? singleNextStep.value.messageId : null,
            shouldCreateNextStep: true,
        });
    } catch (e) {
        messageIsBeingCreated.value = false;
        vm.$notification.error({
            message: i18n.t('Došlo je do greške') as string,
            description: (e as Error).message,
        });
        return;
    }

    messageIsBeingCreated.value = false;
    onCancel();
}

function onCancel() {
    singleNextStep.value = null;
    isNextStepFormActive.value = false;
    firstSelectedUserId.value = null;
}

function showCreateActivityForm(activityFormOptions?: ActivityFormOptions) {
    if (currentUser.value == null) {
        return;
    }

    addEmptyNextStep(activityFormOptions);
    isNextStepFormActive.value = true;
}

function onRemoveFile(file: UploadFile) {
    const index = attachments.value.findIndex((attachment) => attachment.id === file.uid);
    attachments.value.splice(index, 1);
}

function clearAttachments() {
    attachments.value = [];
}

function resetForm() {
    onCancel();
}

EventBus.$on(EventBusEvents.showCreateActivityForm, showCreateActivityForm);
EventBus.$on(EventBusEvents.clearContent, clearAttachments);
EventBus.$on(EventBusEvents.resetChatInputForm, resetForm);

onBeforeUnmount(() => {
    EventBus.$off(EventBusEvents.showCreateActivityForm, showCreateActivityForm);
    EventBus.$off(EventBusEvents.clearContent, clearAttachments);
    EventBus.$off(EventBusEvents.resetChatInputForm, resetForm);
});

watch(
    () => props.selectedProjectId,
    () => {
        firstSelectedUserId.value = null;
        singleNextStep.value = null;
    },
);

return { __sfc: true,props, proxy, vm, attachments, content, isNextStepFormActive, emptyNextStep, firstSelectedUserId, singleNextStep, singleNextStepFormReference, messageIsBeingCreated, allUsers, currentUser, nextStepTypes, reminderOptions, selectUser, addEmptyNextStep, pushToAttachments, onUpdateContent, onSaveMessage, onCreateActivity, onCancel, showCreateActivityForm, onRemoveFile, clearAttachments, resetForm, ChatRichTextEditor, ChatMessageActions, AttachmentList, SingleNextStepForm }
}

})